import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  ContentCopy as CopyIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { ComplexRule, Rule, SimpleRule, RuleColumn } from '../../types/leadSourceRules';
import { RuleRow } from './RuleRow';

interface RuleGroupProps {
  rule: ComplexRule;
  columns: RuleColumn[];
  index: number;
  totalRules: number;
  depth?: number;
  onChange: (rule: ComplexRule) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onCopy: () => void;
}

export const RuleGroup: React.FC<RuleGroupProps> = ({
  rule,
  columns,
  index,
  totalRules,
  depth = 0,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  onCopy,
}) => {
  const borderColor = rule.operator === 'OR' ? '#2196f3' : '#4caf50';
  const bgColor = rule.operator === 'OR' ? '#e3f2fd' : '#e8f5e9';

  // Create a new simple rule
  const createSimpleRule = (): SimpleRule => ({
    type: 'simple',
    column: columns[0]?.column_name || 'origin_host',
    operator: '=',
    value: '',
  });

  // Update operator (AND/OR)
  const handleOperatorChange = (newOperator: 'AND' | 'OR' | null) => {
    if (newOperator) {
      onChange({
        ...rule,
        operator: newOperator,
      });
    }
  };

  // Update nested rule at index
  const updateNestedRule = (nestedIndex: number, updatedRule: Rule) => {
    const newRules = [...rule.rules];
    newRules[nestedIndex] = updatedRule;
    onChange({
      ...rule,
      rules: newRules,
    });
  };

  // Delete nested rule at index
  const deleteNestedRule = (nestedIndex: number) => {
    const newRules = rule.rules.filter((_, i) => i !== nestedIndex);
    // If no rules left, delete the entire group
    if (newRules.length === 0) {
      onDelete();
    } else {
      onChange({
        ...rule,
        rules: newRules,
      });
    }
  };

  // Move nested rule
  const moveNestedRule = (nestedIndex: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? nestedIndex - 1 : nestedIndex + 1;
    if (newIndex < 0 || newIndex >= rule.rules.length) return;
    const newRules = [...rule.rules];
    [newRules[nestedIndex], newRules[newIndex]] = [newRules[newIndex], newRules[nestedIndex]];
    onChange({
      ...rule,
      rules: newRules,
    });
  };

  // Copy nested rule
  const copyNestedRule = (nestedIndex: number) => {
    const ruleCopy = JSON.parse(JSON.stringify(rule.rules[nestedIndex]));
    const newRules = [...rule.rules];
    newRules.splice(nestedIndex + 1, 0, ruleCopy);
    onChange({
      ...rule,
      rules: newRules,
    });
  };

  // Add a new simple rule to this group
  const addRule = () => {
    onChange({
      ...rule,
      rules: [...rule.rules, createSimpleRule()],
    });
  };

  // Add a nested group
  const addNestedGroup = (operator: 'AND' | 'OR') => {
    const nestedGroup: ComplexRule = {
      type: 'complex',
      operator,
      rules: [createSimpleRule()],
    };
    onChange({
      ...rule,
      rules: [...rule.rules, nestedGroup],
    });
  };

  const renderNestedRule = (nestedRule: Rule, nestedIndex: number) => {
    if (nestedRule.type === 'simple') {
      return (
        <RuleRow
          key={nestedIndex}
          rule={nestedRule}
          columns={columns}
          index={nestedIndex}
          totalRules={rule.rules.length}
          onChange={(updated) => updateNestedRule(nestedIndex, updated)}
          onDelete={() => deleteNestedRule(nestedIndex)}
          onMoveUp={() => moveNestedRule(nestedIndex, 'up')}
          onMoveDown={() => moveNestedRule(nestedIndex, 'down')}
          onCopy={() => copyNestedRule(nestedIndex)}
        />
      );
    } else {
      return (
        <RuleGroup
          key={nestedIndex}
          rule={nestedRule}
          columns={columns}
          index={nestedIndex}
          totalRules={rule.rules.length}
          depth={depth + 1}
          onChange={(updated) => updateNestedRule(nestedIndex, updated)}
          onDelete={() => deleteNestedRule(nestedIndex)}
          onMoveUp={() => moveNestedRule(nestedIndex, 'up')}
          onMoveDown={() => moveNestedRule(nestedIndex, 'down')}
          onCopy={() => copyNestedRule(nestedIndex)}
        />
      );
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderLeft: `4px solid ${borderColor}`,
        backgroundColor: bgColor,
        ml: depth > 0 ? 2 : 0,
      }}
    >
      {/* Group Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ToggleButtonGroup
          value={rule.operator}
          exclusive
          size="small"
          onChange={(_, val) => handleOperatorChange(val)}
        >
          <ToggleButton
            value="AND"
            sx={{
              px: 2,
              '&.Mui-selected': {
                backgroundColor: '#4caf50',
                color: 'white',
                '&:hover': { backgroundColor: '#45a049' },
              },
            }}
          >
            AND
          </ToggleButton>
          <ToggleButton
            value="OR"
            sx={{
              px: 2,
              '&.Mui-selected': {
                backgroundColor: '#2196f3',
                color: 'white',
                '&:hover': { backgroundColor: '#1976d2' },
              },
            }}
          >
            OR
          </ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="body2" color="text.secondary">
          Group ({rule.rules.length} {rule.rules.length === 1 ? 'rule' : 'rules'})
        </Typography>

        <Box sx={{ flex: 1 }} />

        {/* Group Action Buttons */}
        <Tooltip title="Move Up">
          <span>
            <IconButton size="small" onClick={onMoveUp} disabled={index === 0}>
              <ArrowUpIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move Down">
          <span>
            <IconButton size="small" onClick={onMoveDown} disabled={index === totalRules - 1}>
              <ArrowDownIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Copy Group">
          <IconButton size="small" onClick={onCopy}>
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete Group">
          <IconButton size="small" onClick={onDelete} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Nested Rules */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {rule.rules.map((nestedRule, nestedIndex) => (
          <Box key={nestedIndex}>
            {nestedIndex > 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textAlign: 'center',
                  py: 0.5,
                  fontWeight: 'bold',
                  color: borderColor,
                }}
              >
                {rule.operator}
              </Typography>
            )}
            {renderNestedRule(nestedRule, nestedIndex)}
          </Box>
        ))}
      </Box>

      {/* Add Buttons */}
      <Box
        sx={{ display: 'flex', gap: 1, mt: 1.5, pt: 1, borderTop: '1px dashed rgba(0,0,0,0.2)' }}
      >
        <Button size="small" startIcon={<AddIcon />} onClick={addRule}>
          Add Rule
        </Button>
        {depth < 2 && ( // Limit nesting depth to 3 levels
          <>
            <Button size="small" variant="outlined" onClick={() => addNestedGroup('AND')}>
              Add AND Group
            </Button>
            <Button size="small" variant="outlined" onClick={() => addNestedGroup('OR')}>
              Add OR Group
            </Button>
          </>
        )}
      </Box>
    </Paper>
  );
};
